/**
 * ehr#681: the client drawer's save confirmation. After Save draft / Save record a white card with a
 * check says "Draft saved" / "Record saved" just above the button that was clicked (bottom right),
 * then goes after 2.5 s or on its ×. A failed save shows the same card with a red accent, "Not
 * saved" and the existing error text, and stays 6 s. On a phone (or a narrow touch screen) the card
 * becomes a bottom sheet across the screen (styles.css .save-toast). It replaces the old
 * "Saved ✓" text on the footer's left. Colors come from the theme tokens, so dark mode follows.
 *
 * The same card and "Saving…" button on the other save screens (Follow-up, a progress note's Save
 * Draft, Settings > Sync settings / Payors / CPT codes, the drawer's Authorizations): `SaveButton`
 * for one save button, `SaveCardAnchor` to hang the card on any element, `okNotice` / `errNotice`
 * for its copy, and `runSave` for the at-least-2-s "Saving…" and the card that follows. Where the
 * save closes the form it was made in, the card shows under the button that opened that form
 * (`below`), pointing up.
 */
import { useEffect, type ReactNode } from 'react';
import { SAVE_MIN_MS, atLeast } from '../lib/saveTiming.js';

export type SaveNotice = {
  /** New id per save, so a second save restarts the timer and the entrance. */
  id: number;
  ok: boolean;
  /** Which button the card sits on: 'draft' / 'record' in the drawer footer, any name elsewhere. */
  at: string;
  /** Success: the headline. Failure: what failed (the save bar's error text). */
  text: string;
  /** Under its anchor, pointing up (the form that saved has closed), instead of above it. */
  below?: boolean;
};

/** How long each card stays (ms). */
export const SAVE_TOAST_OK_MS = 2500;
export const SAVE_TOAST_ERR_MS = 6000;

let seq = 0;
export function savedNotice(draft: boolean): SaveNotice {
  return {
    id: ++seq,
    ok: true,
    at: draft ? 'draft' : 'record',
    text: draft ? 'Draft saved' : 'Record saved',
  };
}
export function failedNotice(draft: boolean, text: string): SaveNotice {
  return { id: ++seq, ok: false, at: draft ? 'draft' : 'record', text };
}

type Place = { at?: string; below?: boolean };
/** Any screen's success card; `text` says what was saved ("Follow-up saved"). */
export function okNotice(text: string, place: Place = {}): SaveNotice {
  return { id: ++seq, ok: true, at: place.at ?? 'save', text, ...(place.below && { below: true }) };
}
/** Any screen's "Not saved" card; `text` is the reason. */
export function errNotice(text: string, place: Place = {}): SaveNotice {
  return {
    id: ++seq,
    ok: false,
    at: place.at ?? 'save',
    text,
    ...(place.below && { below: true }),
  };
}

export type SaveResult<T> =
  { ok: true; value: T; notice: SaveNotice } | { ok: false; error: unknown; notice: SaveNotice };

/**
 * A save with the drawer's timing: settles no sooner than SAVE_MIN_MS after the call (the button
 * keeps "Saving…" that long), then hands back the card to show. Never rejects.
 */
export function runSave<T>(
  p: Promise<T>,
  copy: {
    ok: string | ((v: T) => string);
    err: (e: unknown) => string;
    at?: string;
    /** The success card goes under the button that opened the (now closed) form. */
    okBelow?: boolean;
  },
  minMs = SAVE_MIN_MS,
): Promise<SaveResult<T>> {
  return atLeast(p, minMs).then(
    (value) => ({
      ok: true as const,
      value,
      notice: okNotice(typeof copy.ok === 'function' ? copy.ok(value) : copy.ok, {
        at: copy.at,
        below: copy.okBelow,
      }),
    }),
    (error: unknown) => ({
      ok: false as const,
      error,
      notice: errNotice(copy.err(error), { at: copy.at }),
    }),
  );
}

export function SaveToast({ notice, onClose }: { notice: SaveNotice; onClose: () => void }) {
  useEffect(() => {
    const t = window.setTimeout(onClose, notice.ok ? SAVE_TOAST_OK_MS : SAVE_TOAST_ERR_MS);
    return () => window.clearTimeout(t);
    // onClose is a fresh arrow each render; the timer belongs to this notice only.
  }, [notice.id]);
  return (
    <div
      className={`save-toast ${notice.ok ? 'is-ok' : 'is-err'} at-${notice.at}${notice.below ? ' is-below' : ''}`}
      role={notice.ok ? 'status' : 'alert'}
      aria-live={notice.ok ? 'polite' : 'assertive'}
      aria-atomic="true"
    >
      <span className="save-toast-icon" aria-hidden>
        {notice.ok ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="7" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        )}
      </span>
      <span className="save-toast-body">
        <strong className="save-toast-title">{notice.ok ? notice.text : 'Not saved'}</strong>
        {!notice.ok && <span className="save-toast-detail">{notice.text}</span>}
      </span>
      <button type="button" className="save-toast-x" aria-label="Dismiss" onClick={onClose}>
        ×
      </button>
    </div>
  );
}

/**
 * ehr#681 (Taras, 10-29): the drawer's Save draft / Save record pair. While a save runs the clicked
 * button turns mint, reads "Saving…" and both are disabled (no double save); the card above sits on
 * the clicked button; the "Record / Draft last saved X ago" line sits under the pair.
 */
export function SaveButtons({
  savingAt,
  disabled,
  notice,
  onCloseNotice,
  lastLine,
  onSave,
  onCancel,
  cancelDisabled = false,
}: {
  savingAt: 'draft' | 'record' | null;
  disabled: boolean;
  notice: SaveNotice | null;
  onCloseNotice: () => void;
  lastLine: string;
  onSave: (draft: boolean) => void;
  /** ehr#743 (V11): a Cancel button right before the pair; it discards the unsaved changes. Its
   * "Changes discarded" card is a notice `at: 'cancel'`. */
  onCancel?: () => void;
  /** Nothing to discard. */
  cancelDisabled?: boolean;
}) {
  const one = (kind: 'draft' | 'record') => {
    const busy = savingAt === kind;
    return (
      <span className="save-toast-anchor">
        <button
          type="button"
          className={`btn ${kind === 'record' ? 'btn-primary' : 'btn-ghost'}${busy ? ' is-saving' : ''}`}
          onClick={() => onSave(kind === 'draft')}
          disabled={disabled || savingAt !== null}
          aria-busy={busy || undefined}
        >
          {busy && <span className="save-spin" aria-hidden />}
          {busy ? 'Saving…' : kind === 'record' ? 'Save record' : 'Save draft'}
        </button>
        {notice?.at === kind && (
          <SaveToast key={notice.id} notice={notice} onClose={onCloseNotice} />
        )}
      </span>
    );
  };
  return (
    <>
      {onCancel && (
        <span className="save-toast-anchor">
          <button
            type="button"
            className="btn btn-ghost save-cancel"
            onClick={onCancel}
            disabled={cancelDisabled || disabled || savingAt !== null}
          >
            Cancel
          </button>
          {notice?.at === 'cancel' && (
            <SaveToast key={notice.id} notice={notice} onClose={onCloseNotice} />
          )}
        </span>
      )}
      {one('draft')}
      {one('record')}
      {lastLine && <span className="save-last">{lastLine}</span>}
    </>
  );
}

/** Hangs the save card on any element: a toolbar button, a switch. */
export function SaveCardAnchor({
  notice,
  onClose,
  children,
}: {
  notice: SaveNotice | null;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <span className="save-toast-anchor">
      {children}
      {notice && <SaveToast key={notice.id} notice={notice} onClose={onClose} />}
    </span>
  );
}

/**
 * One save button with the drawer's behaviour: while `busy` it reads "Saving…" with the spinner
 * (mint) and is disabled; the card sits above it.
 */
export function SaveButton({
  label,
  busy,
  disabled = false,
  notice,
  onCloseNotice,
  onClick,
  className = 'btn primary',
  id,
}: {
  label: string;
  busy: boolean;
  disabled?: boolean;
  notice: SaveNotice | null;
  onCloseNotice: () => void;
  onClick: () => void;
  className?: string;
  id?: string;
}) {
  return (
    <SaveCardAnchor notice={notice} onClose={onCloseNotice}>
      <button
        type="button"
        id={id}
        className={`${className}${busy ? ' is-saving' : ''}`}
        onClick={onClick}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
      >
        {busy && <span className="save-spin" aria-hidden />}
        {busy ? 'Saving…' : label}
      </button>
    </SaveCardAnchor>
  );
}
